import axios from 'axios'

// Cookie 鉴权：withCredentials 必须为 true，浏览器才会带上 HttpOnly cookie
const client = axios.create({
  baseURL: '/api',
  timeout: 30000,
  withCredentials: true,
})

// R-fix-2026-08-19: 401 时自动用 refresh_token 换新 access_token,
// 避免每 15 分钟(原 5 天)把用户踢回登录页。
// 并发请求去重:同一时刻只发一次 refresh,其他等结果。
let _refreshInflight = null
async function _tryRefresh() {
  if (_refreshInflight) return _refreshInflight
  _refreshInflight = axios.post('/api/auth/refresh', null, {
    baseURL: '/api',
    withCredentials: true,
    timeout: 10000,
  }).then(r => {
    _refreshInflight = null
    return r
  }).catch(e => {
    _refreshInflight = null
    throw e
  })
  return _refreshInflight
}

// R8-A2-P0-5: 拦截器把后端 X-Request-ID / X-Response-Time 写到 err 上,
// 供 message.error 和 queryCache.onError 报告给用户 + 控制台
client.interceptors.response.use(
  (response) => {
    // R-audit-2026-09-08 FE-P0-01: 14 个页面 useQuery + axios.get().then(r=>r.data)
    // 裸调没走 useApiQuery 归一 — 后端返 200 + {items, total, _permission_denied}
    // 时 r.data 是对象,前端 .map 直接 TypeError 白屏。
    // 统一在 axios 拦截器归一:data.items 数组透出,丢失 total(可接受, 业务页通常不展示)。
    const data = response?.data
    if (data && typeof data === 'object' && !Array.isArray(data)
        && '_permission_denied' in data && Array.isArray(data.items)) {
      response.data = data.items
      response._permissionDenied = data._permission_denied
      response._total = data.total
    }
    return response
  },
  async (error) => {
    try {
      const rid = error?.response?.headers?.['x-request-id']
      const rt = error?.response?.headers?.['x-response-time']
      if (rid) error._rid = rid
      if (rt) error._rt = rt

      const status = error?.response?.status
      const originalRequest = error?.config
      const isAuthEndpoint = originalRequest?.url?.includes('/api/auth/')
      const onLoginPage = window.location.pathname.startsWith('/login')

      // 401 + 不是 /api/auth/ 端点(避免 refresh 失败时无限循环)
      // + 没在登录页(登录页 401 让用户看到错误)
      // + 还没尝试过 refresh(防止 _retry 循环)
      if (status === 401 && !isAuthEndpoint && !onLoginPage && !originalRequest?._retried) {
        try {
          await _tryRefresh()
          originalRequest._retried = true
          // 用新 cookie 重发原请求
          return client(originalRequest)
        } catch (_) {
          // refresh 也失败(过期/被踢) → 跳登录页
          import('../hooks/useAuth').then(({ isLoggingOut, isLoggingIn }) => {
            if (!isLoggingOut() && !isLoggingIn()) window.location.href = '/login?expired=1'
          })
        }
      } else if (status === 401 && !onLoginPage) {
        // 兜底:登录过渡态 / logout 中不跳
        import('../hooks/useAuth').then(({ isLoggingOut, isLoggingIn }) => {
          if (!isLoggingOut() && !isLoggingIn()) window.location.href = '/login?expired=1'
        })
      }
    } catch (_) { /* 拦截器里不抛,避免掩盖真错 */ }
    return Promise.reject(error)
  }
)

export default client
